import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Capacitor, SystemBars, SystemBarsStyle, SystemBarType } from "@capacitor/core";
import { App as NativeApp } from "@capacitor/app";
import { Keyboard, KeyboardResize } from "@capacitor/keyboard";
import { Haptics, ImpactStyle, NotificationType } from "@capacitor/haptics";
import { Network } from "@capacitor/network";
import { FirebaseMessaging, Importance, Visibility } from "@capacitor-firebase/messaging";
import axios from "axios";
import { useConfirm } from "./ConfirmDialog";
import { useTheme } from "../theme/ThemeProvider";

const isNative = () => Capacitor.isNativePlatform();
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
let lastHapticAt = 0;

const impact = async (style = ImpactStyle.Light) => {
  if (!isNative() || Date.now() - lastHapticAt < 60) return;
  lastHapticAt = Date.now();
  try {
    await Haptics.impact({ style });
  } catch {
    // Haptics are optional on unsupported devices.
  }
};

export const notifyHaptic = async (type = "success") => {
  if (!isNative()) return;
  const mapped = {
    success: NotificationType.Success,
    error: NotificationType.Error,
    warning: NotificationType.Warning,
  };
  try {
    await Haptics.notification({ type: mapped[type] || NotificationType.Success });
  } catch {
    // Haptics are optional on unsupported devices.
  }
};

const getDirtyForm = () =>
  document.querySelector('form[data-protect-unsaved="true"][data-dirty="true"]');

const dismissTopLayer = () => {
  const dialog = document.querySelector('[role="dialog"][data-state="open"]');
  if (dialog) {
    document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true }));
    return true;
  }
  const sidebarToggle = document.querySelector('[data-testid="sidebar-mobile-toggle"][aria-expanded="true"]');
  if (sidebarToggle) {
    window.dispatchEvent(new CustomEvent("crickhere:native-back"));
    return true;
  }
  return false;
};

export const NativeMobileRuntime = () => {
  const confirm = useConfirm();
  const location = useLocation();
  const { resolvedTheme } = useTheme();

  useEffect(() => {
    const root = document.documentElement;
    let largestViewport = window.innerHeight;
    let previousWidth = window.innerWidth;
    const focusTimers = new Set();

    const keepFocusedControlVisible = () => {
      const field = document.activeElement;
      if (!field?.matches?.("input, textarea, select, [contenteditable=true]")) return;
      field.scrollIntoView?.({ block: "center", inline: "nearest", behavior: "smooth" });
    };

    const scheduleFocusVisibility = () => {
      [50, 180, 360].forEach((delay) => {
        const timer = window.setTimeout(() => {
          focusTimers.delete(timer);
          keepFocusedControlVisible();
        }, delay);
        focusTimers.add(timer);
      });
    };

    const updateViewport = () => {
      const viewport = window.visualViewport;
      const height = Math.max(1, viewport?.height || window.innerHeight);
      const top = Math.max(0, viewport?.offsetTop || 0);
      if (Math.abs(window.innerWidth - previousWidth) > 80) {
        largestViewport = height;
        previousWidth = window.innerWidth;
      }
      largestViewport = Math.max(largestViewport, window.innerHeight, height);
      root.style.setProperty("--app-viewport-height", `${Math.round(height)}px`);
      root.style.setProperty("--app-viewport-top", `${Math.round(top)}px`);
      root.style.setProperty("--app-viewport-center", `${Math.round(top + height / 2)}px`);

      // Mobile browsers do not emit Capacitor keyboard events. Detect their
      // resized visual viewport so the same form and navigation rules apply.
      if (!isNative()) {
        const keyboardVisible = largestViewport - height > 120;
        root.classList.toggle("keyboard-open", keyboardVisible);
        root.style.setProperty(
          "--keyboard-height",
          `${Math.max(0, Math.round(largestViewport - height - top))}px`,
        );
      }
    };

    updateViewport();
    window.addEventListener("resize", updateViewport);
    window.addEventListener("orientationchange", updateViewport);
    window.visualViewport?.addEventListener("resize", updateViewport);
    window.visualViewport?.addEventListener("scroll", updateViewport);
    document.addEventListener("focusin", scheduleFocusVisibility);
    return () => {
      window.removeEventListener("resize", updateViewport);
      window.removeEventListener("orientationchange", updateViewport);
      window.visualViewport?.removeEventListener("resize", updateViewport);
      window.visualViewport?.removeEventListener("scroll", updateViewport);
      document.removeEventListener("focusin", scheduleFocusVisibility);
      focusTimers.forEach(window.clearTimeout);
      if (!isNative()) root.classList.remove("keyboard-open");
    };
  }, []);

  useEffect(() => {
    if (!isNative()) return undefined;
    document.documentElement.classList.add("native-shell");

    let cancelled = false;
    let appStateHandle;
    const timers = [];
    const style = resolvedTheme === "light" ? SystemBarsStyle.Light : SystemBarsStyle.Dark;

    const applySystemBarStyle = async () => {
      if (cancelled) return;
      // Some devices restore their native default after resume, so set both bars explicitly.
      await Promise.allSettled([
        SystemBars.setStyle({ style, bar: SystemBarType.StatusBar }),
        SystemBars.setStyle({ style, bar: SystemBarType.NavigationBar }),
      ]);
    };

    applySystemBarStyle();
    timers.push(window.setTimeout(applySystemBarStyle, 100));
    timers.push(window.setTimeout(applySystemBarStyle, 500));
    NativeApp.addListener("appStateChange", ({ isActive }) => {
      if (isActive) applySystemBarStyle();
    }).then((handle) => {
      appStateHandle = handle;
    }).catch(() => {});

    return () => {
      cancelled = true;
      timers.forEach(window.clearTimeout);
      appStateHandle?.remove?.();
      document.documentElement.classList.remove("native-shell");
    };
  }, [resolvedTheme]);

  useEffect(() => {
    if (!isNative()) return undefined;
    let active = true;
    const handles = [];

    const setup = async () => {
      try {
        await Keyboard.setResizeMode({ mode: KeyboardResize.Native });
        await Keyboard.setScroll({ isDisabled: false });
      } catch {
        // Keyboard APIs vary by platform and OS version.
      }

      handles.push(await Keyboard.addListener("keyboardWillShow", ({ keyboardHeight }) => {
        document.documentElement.style.setProperty("--keyboard-height", keyboardHeight + "px");
        document.documentElement.classList.add("keyboard-open");
        [120, 300].forEach((delay) => window.setTimeout(() => {
          document.activeElement?.scrollIntoView?.({ block: "center", inline: "nearest", behavior: "smooth" });
        }, delay));
      }));
      handles.push(await Keyboard.addListener("keyboardDidShow", () => {
        window.dispatchEvent(new Event("resize"));
        document.activeElement?.scrollIntoView?.({ block: "center", inline: "nearest", behavior: "smooth" });
      }));
      handles.push(await Keyboard.addListener("keyboardWillHide", () => {
        document.documentElement.style.setProperty("--keyboard-height", "0px");
        document.documentElement.classList.remove("keyboard-open");
        document.scrollingElement?.scrollBy?.({ top: 0, behavior: "instant" });
      }));
      handles.push(await Keyboard.addListener("keyboardDidHide", () => {
        // iOS WKWebView and some Android WebViews can retain the keyboard's
        // visualViewport offset after blur. Force the shared viewport owner to
        // sample the restored screen instead of carrying that offset forward.
        document.documentElement.style.setProperty("--app-viewport-top", "0px");
        document.documentElement.style.setProperty(
          "--app-viewport-center",
          `${Math.round((window.visualViewport?.height || window.innerHeight) / 2)}px`,
        );
        window.setTimeout(() => window.dispatchEvent(new Event("resize")), 50);
        window.setTimeout(() => window.dispatchEvent(new Event("resize")), 250);
      }));
      handles.push(await Network.addListener("networkStatusChange", (status) => {
        window.dispatchEvent(new CustomEvent("crickhere:network", { detail: status }));
      }));
      const initialNetwork = await Network.getStatus();
      window.dispatchEvent(new CustomEvent("crickhere:network", { detail: initialNetwork }));
      handles.push(await NativeApp.addListener("backButton", async ({ canGoBack }) => {
        const dirtyForm = getDirtyForm();
        if (dirtyForm) {
          const leave = await confirm("Your unsaved changes will be lost.", {
            title: "Discard changes?",
            destructive: true,
          });
          if (!leave) return;
          dirtyForm.removeAttribute("data-dirty");
        }
        if (dismissTopLayer()) return;
        if (canGoBack || window.history.length > 1) {
          window.history.back();
        } else {
          NativeApp.exitApp();
        }
      }));
    };

    setup().catch(() => {});
    return () => {
      active = false;
      handles.forEach((handle) => handle?.remove?.());
      if (!active) document.documentElement.classList.remove("keyboard-open");
    };
  }, [confirm]);

  useEffect(() => {
    if (!isNative()) return undefined;
    let cancelled = false;
    const handles = [];
    const platform = Capacitor.getPlatform();

    const saveToken = async (token) => {
      if (!token || cancelled) return;
      await axios.post(
        `${API}/push/native-token`,
        { token, platform },
        { withCredentials: true }
      ).catch(() => {});
    };

    const setupNativePush = async () => {
      const support = await FirebaseMessaging.isSupported();
      if (!support.isSupported) return;
      if (platform === "android") {
        await FirebaseMessaging.createChannel({
          id: "match_reminders",
          name: "Match reminders",
          description: "Scheduled and delayed match alerts",
          importance: Importance.High,
          visibility: Visibility.Public,
          vibration: true,
          sound: "default",
          lights: true,
          lightColor: "#CCFF00",
        }).catch(() => {});
      }
      let permission = await FirebaseMessaging.checkPermissions();
      if (permission.receive === "prompt" || permission.receive === "prompt-with-rationale") {
        permission = await FirebaseMessaging.requestPermissions();
      }
      if (permission.receive !== "granted") return;

      const result = await FirebaseMessaging.getToken();
      await saveToken(result.token);
      handles.push(await FirebaseMessaging.addListener("tokenReceived", ({ token }) => saveToken(token)));
      handles.push(await FirebaseMessaging.addListener("notificationActionPerformed", ({ notification }) => {
        const target = notification?.data?.url;
        if (target && typeof target === "string") window.location.assign(target);
      }));
    };

    setupNativePush().catch(() => {});
    return () => {
      cancelled = true;
      handles.forEach((handle) => handle?.remove?.());
    };
  }, [location.pathname]);

  useEffect(() => {
    const markDirty = (event) => {
      const form = event.target.closest?.('form[data-protect-unsaved="true"]');
      if (form) form.dataset.dirty = "true";
    };
    const handleBeforeUnload = (event) => {
      if (!getDirtyForm()) return;
      event.preventDefault();
      event.returnValue = "";
    };
    const handleTap = (event) => {
      const control = event.target.closest?.("button, [role='button']");
      if (control && !control.disabled && control.getAttribute("aria-disabled") !== "true") impact();
    };

    document.addEventListener("input", markDirty, true);
    document.addEventListener("change", markDirty, true);
    document.addEventListener("pointerup", handleTap, true);
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      document.removeEventListener("input", markDirty, true);
      document.removeEventListener("change", markDirty, true);
      document.removeEventListener("pointerup", handleTap, true);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    const prepareImage = (image) => {
      if (!(image instanceof HTMLImageElement)) return;
      image.decoding = "async";
      if (!image.hasAttribute("loading")) image.loading = "lazy";
    };
    const prepareTree = (root) => {
      if (root instanceof HTMLImageElement) prepareImage(root);
      root.querySelectorAll?.("img").forEach(prepareImage);
    };
    const handleImageError = (event) => {
      if (event.target instanceof HTMLImageElement) {
        event.target.dataset.imageFailed = "true";
      }
    };
    prepareTree(document);
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => mutation.addedNodes.forEach(prepareTree));
    });
    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener("error", handleImageError, true);
    return () => {
      observer.disconnect();
      document.removeEventListener("error", handleImageError, true);
    };
  }, []);

  useEffect(() => {
    if (!isNative()) return undefined;
    const seen = new WeakSet();
    const observer = new MutationObserver(() => {
      document.querySelectorAll("[data-sonner-toast]").forEach((toastNode) => {
        if (seen.has(toastNode)) return;
        seen.add(toastNode);
        const type = toastNode.getAttribute("data-type") || "success";
        if (type === "error" || type === "warning" || type === "success") {
          notifyHaptic(type);
        }
        if (type === "success") {
          document.querySelectorAll('form[data-protect-unsaved="true"]').forEach((form) => {
            form.removeAttribute("data-dirty");
          });
        }
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    document.querySelectorAll('form[data-protect-unsaved="true"]').forEach((form) => {
      form.removeAttribute("data-dirty");
    });
  }, [location.pathname]);

  return null;
};
